import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, QrCode } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { MaskedLines, Reveal, FadeIn } from "../components/Motion";
import Marquee from "../components/Marquee";
import { ASSETS } from "../i18n/content";
import { InsightsStrip } from "./Insights";

const CertificoQrBox = ({ compact = false }) => {
  const { t } = useLanguage();
  const c = t.home.certifico;
  return (
    <motion.div
      data-testid="certificoqr-spotlight"
      whileHover={{ y: -6 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={`relative flex flex-col justify-between overflow-hidden rounded-2xl bg-[#0A0F1A] p-8 text-white shadow-2xl ring-1 ring-white/10 ${
        compact ? "" : "col-span-12 lg:col-span-4 lg:row-span-2"
      }`}
    >
      <div
        className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 opacity-[0.13]"
        aria-hidden="true"
      >
        <svg viewBox="0 0 100 100" fill="none" className="h-full w-full">
          {Array.from({ length: 8 }).map((_, r) =>
            Array.from({ length: 8 }).map((_, cIdx) =>
              (r * 8 + cIdx) % 3 !== 0 ? (
                <rect key={`${r}-${cIdx}`} x={cIdx * 12.5} y={r * 12.5} width="9" height="9" fill="#10B981" />
              ) : null
            )
          )}
        </svg>
      </div>
      <div>
        <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-1.5 font-mono text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-emerald-400">
          <QrCode className="h-3.5 w-3.5" />
          {c.badge}
        </span>
        <h3 className="mt-6 font-display text-3xl font-bold tracking-tight">{c.title}</h3>
        <p className="mt-4 text-sm leading-relaxed text-white/65">{c.text}</p>
      </div>
      <a
        href="https://certificoqr.com"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="certificoqr-cta"
        className="group mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-emerald-500 px-5 py-3 text-sm font-semibold text-[#04170F] transition-colors duration-300 hover:bg-emerald-400"
      >
        {c.cta}
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </a>
    </motion.div>
  );
};

const ServiceCard = ({ service, wide = false, delay = 0 }) => {
  const { t } = useLanguage();
  return (
    <motion.div
      whileHover={{ y: -6 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={`col-span-12 ${wide ? "md:col-span-6" : "md:col-span-6 lg:col-span-4"}`}
    >
      <Link
        to={service.path}
        data-testid={`service-card-${service.slug}`}
        className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow duration-500 hover:shadow-xl"
      >
        {wide && (
          <div className="h-44 overflow-hidden">
            <img
              src={service.image}
              alt={service.title}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </div>
        )}
        <div className="flex flex-1 flex-col p-7">
          <h3 className="font-display text-xl font-semibold tracking-tight text-ink">
            {service.title}
          </h3>
          <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-500">{service.short}</p>
          <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-navy">
            {t.common.discover}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const h = t.home;

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-paper pt-32 md:pt-40">
      <div className="blueprint-grid absolute inset-0 opacity-60" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-14 pb-16 md:pb-24 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeIn>
              <p className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.3em] text-accent">
                {h.heroOverline}
              </p>
            </FadeIn>
            <h1 className="mt-6 font-display text-4xl font-bold leading-[1.04] tracking-tight text-ink sm:text-5xl lg:text-[4.2rem]">
              <MaskedLines lines={h.heroLines} />
            </h1>
            <FadeIn delay={0.65}>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
                {h.heroSub}
              </p>
            </FadeIn>
            <FadeIn delay={0.8}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/servizi"
                  data-testid="hero-cta-primary"
                  className="group flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-navy-dark"
                >
                  {h.heroCtaPrimary}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/regolamento-ue"
                  data-testid="hero-cta-secondary"
                  className="group flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-navy transition-colors duration-300 hover:border-navy"
                >
                  {h.heroCtaSecondary}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </FadeIn>
          </div>

          <div className="lg:col-span-5">
            <FadeIn delay={0.5}>
              <motion.div style={{ y: imgY }} className="relative">
                <div className="overflow-hidden rounded-2xl shadow-2xl [clip-path:polygon(0_0,100%_0,100%_88%,88%_100%,0_100%)]">
                  <img
                    src={ASSETS.blueprint}
                    alt="Blueprint tecnico Aldebran"
                    className="h-[26rem] w-full object-cover lg:h-[30rem]"
                  />
                </div>
                <div className="absolute -bottom-5 -left-5 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-xl">
                  <p className="font-display text-2xl font-bold text-navy">{h.stats[0].value}</p>
                  <p className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-slate-400">
                    {h.stats[0].label}
                  </p>
                </div>
              </motion.div>
            </FadeIn>
          </div>
        </div>
      </div>

      <div className="relative border-t border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-4 py-10 sm:px-6 lg:grid-cols-4 lg:px-8">
          {h.stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08} data-testid={`stat-${i}`}>
              <div data-testid={`stat-${i}`}>
                <p className="font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">{s.value}</p>
                <p className="mt-1.5 font-mono text-[0.62rem] uppercase tracking-[0.2em] text-slate-400">
                  {s.label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const AboutStrip = () => {
  const { t } = useLanguage();
  const h = t.home;
  return (
    <section data-testid="about-section" className="bg-white py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-accent">
              {h.aboutOverline}
            </p>
            <h2 className="mt-5 font-display text-2xl font-semibold leading-snug tracking-tight text-ink sm:text-3xl lg:text-4xl">
              {h.aboutTitle}
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">{h.aboutText}</p>
            <Link
              to="/contatti"
              data-testid="about-cta"
              className="group mt-8 inline-flex items-center gap-2 font-semibold text-navy transition-colors duration-300 hover:text-accent"
            >
              {h.aboutCta}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-xl">
              <img
                src={ASSETS.cadEngineer}
                alt="Ingegnere Aldebran alla workstation CAD"
                loading="lazy"
                className="h-80 w-full object-cover md:h-96"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const ServicesBento = () => {
  const { t } = useLanguage();
  const s = t.services;
  const by = (slug) => s.find((x) => x.slug === slug);
  return (
    <section data-testid="services-bento" className="bg-mist py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-accent">
            {t.home.servicesOverline}
          </p>
          <h2 className="mt-5 max-w-2xl font-display text-2xl font-semibold leading-snug tracking-tight text-ink sm:text-3xl lg:text-4xl">
            {t.home.servicesTitle}
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600">{t.home.servicesSub}</p>
        </Reveal>

        <div className="mt-14 grid grid-cols-12 gap-5">
          <motion.div
            whileHover={{ y: -6 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 lg:col-span-8"
          >
            <Link
              to={by("manualistica").path}
              data-testid="service-card-manualistica"
              className="group relative flex h-full min-h-[22rem] flex-col justify-end overflow-hidden rounded-2xl shadow-lg"
            >
              <img
                src={by("manualistica").image}
                alt={by("manualistica").title}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/90 via-navy-dark/35 to-transparent" />
              <div className="relative p-8">
                <h3 className="font-display text-2xl font-semibold tracking-tight text-white md:text-3xl">
                  {by("manualistica").title}
                </h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-white/75">
                  {by("manualistica").short}
                </p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-white">
                  {t.common.discover}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </motion.div>

          <CertificoQrBox />

          {["risk-analysis", "regolamento-ue", "certificazioni-e-sicurezza"].map((slug, i) => (
            <ServiceCard key={slug} service={by(slug)} delay={i * 0.06} />
          ))}
          {["documentazione-3d", "industria-4-5", "project-management", "ai"].map((slug) => (
            <ServiceCard key={slug} service={by(slug)} wide />
          ))}
        </div>
      </div>
    </section>
  );
};

const LiveCountdown = () => {
  const { lang } = useLanguage();
  const target = new Date(2027, 0, 20).getTime();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target - now);
  const units = [
    Math.floor(diff / 86400000),
    Math.floor((diff % 86400000) / 3600000),
    Math.floor((diff % 3600000) / 60000),
  ];
  const labels = lang === "it" ? ["giorni", "ore", "minuti"] : ["days", "hours", "minutes"];
  return (
    <div className="flex items-end gap-5" data-testid="regulation-countdown-live">
      {units.map((u, i) => (
        <div key={i} className="text-center">
          <p className="font-display text-5xl font-bold tabular-nums tracking-tight text-white md:text-6xl">
            {String(u).padStart(2, "0")}
          </p>
          <p className="mt-2 font-mono text-[0.6rem] uppercase tracking-[0.24em] text-white/50">
            {labels[i]}
          </p>
        </div>
      ))}
    </div>
  );
};

const RegulationBand = () => {
  const { t } = useLanguage();
  return (
    <section data-testid="regulation-band" className="bg-navy py-20 lg:py-24">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-8">
          <Reveal>
            <p className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-[#4DA3FF]">
              {t.home.regOverline}
            </p>
            <h2 className="mt-5 font-display text-2xl font-semibold leading-snug tracking-tight text-white sm:text-3xl lg:text-4xl">
              {t.home.regTitle}
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70">{t.home.regText}</p>
            <Link
              to="/regolamento-ue"
              data-testid="regulation-cta"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-navy transition-colors duration-300 hover:bg-slate-100"
            >
              {t.home.regCta}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-4">
          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-white/15 bg-white/[0.04] p-8" data-testid="regulation-countdown">
              <LiveCountdown />
              <p className="mt-5 border-t border-white/10 pt-4 font-display text-lg font-semibold text-[#E8912D]">
                20.01.2027
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const CtaBand = () => {
  const { t } = useLanguage();
  return (
    <section data-testid="home-cta-section" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-8 rounded-2xl border border-slate-200 bg-mist px-8 py-12 md:flex-row md:items-center md:px-14 md:py-16">
            <div>
              <h2 className="max-w-xl font-display text-2xl font-semibold leading-snug tracking-tight text-ink sm:text-3xl lg:text-4xl">
                {t.home.ctaTitle}
              </h2>
              <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-600">{t.home.ctaText}</p>
            </div>
            <Link
              to="/contatti"
              data-testid="home-cta-button"
              className="group flex shrink-0 items-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-navy-dark"
            >
              {t.home.ctaButton}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  const { t } = useLanguage();
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee items={t.home.marquee} />
      <AboutStrip />
      <ServicesBento />
      <RegulationBand />
      <InsightsStrip />
      <CtaBand />
    </main>
  );
}
